import React, {
  ComponentType,
  createContext,
  JSXElementConstructor,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  useContext,
} from "react";
import { makeAutoObservable } from "mobx";
import { Direction } from "src/grid/Direction";
import { Instruction } from "./Instruction";
import { GridLocation, GridLocationHash } from "src/grid/GridLocation";
import { advanceIndexInDirection } from "src/grid/advanceIndexInDirection";
import { instructions, InstructionType } from "./instructionData";
import { isDefined } from "src/functional/isDefined";

export class ProgramModel {
  public gridDimensions = {
    width: 10,
    height: 10,
  };
  public reference: string[] = [];
  public mana: number = 0;
  public flux: number = 0;
  public cursorIndex: number = 0;
  public cursorDirection: Direction = "right";
  public instructions: Map<GridLocationHash, Instruction> = new Map();
  public selectedPaletteInstruction: InstructionType = instructions[0].type;

  constructor() {
    makeAutoObservable(this);
  }

  get cursorGridLocation() {
    return GridLocation.fromGridIndex(this.cursorIndex, this.gridDimensions);
  }

  step() {
    const location = GridLocation.fromGridIndex(
      this.cursorIndex,
      this.gridDimensions
    );
    const instruction = this.instructions.get(location.toHash());

    const instructionResult = instruction?.act(this);
    const shouldAdvanceCursor =
      !instructionResult || instructionResult.shouldAdvanceCursor;

    if (shouldAdvanceCursor) {
      const newIndex = advanceIndexInDirection(
        this.cursorIndex,
        this.cursorDirection,
        this.gridDimensions
      );
      this.cursorIndex = newIndex;
    }
  }

  getNeighboringInstructions(location: GridLocation) {
    return [
      location.addDirection("up"),
      location.addDirection("down"),
      location.addDirection("left"),
      location.addDirection("right"),
    ]
      .map((location) => this.instructions.get(location.toHash()))
      .filter(isDefined);
  }

  getInstructionAtLocation(location: GridLocation) {
    return this.instructions.get(location.toHash());
  }
}

export const program = new ProgramModel();
export const ProgramContext = createContext(program);

export const ProgramContextProvider = ({ children }: PropsWithChildren) => {
  return (
    <ProgramContext.Provider value={program}>
      {children}
    </ProgramContext.Provider>
  );
};

export const useProgram = () => {
  const program = useContext(ProgramContext);
  return program;
};

export function withProgramContext<P extends object>(
  WrappedComponent: ComponentType<P>
) {
  return function (props: P) {
    return (
      <ProgramContextProvider>
        <WrappedComponent {...props} />
      </ProgramContextProvider>
    );
  };
}
